import { ArrowDownOutlined, ArrowUpOutlined } from "@ant-design/icons"
import { Button, Tooltip } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { homeColor, homeText } from "../home-theme.ts"
import {
  HOME_SECTION_SPECS,
  type HomeSectionId,
  sectionAnchorId
} from "./homeSectionModel.ts"
import { WidgetDragHandle } from "./Portfolio/WidgetEditChrome"

/**
 * The sticky section nav overlaps whatever a jump lands on, so the heading
 * reserves the nav's height above itself (CSS, so a plain `#hash` load works
 * too).
 */
const SCROLL_MARGIN = 64

interface HomeSectionHeadingProps {
  id: HomeSectionId
  /** Replaces the standing caption with something only this render knows. */
  caption?: React.ReactNode
  /** Sits at the heading's right edge — "View all actions", "Open Locations". */
  action?: React.ReactNode
  /** Edit layout: the section can be dragged, or moved one place at a time. */
  editing?: {
    canMoveUp: boolean
    canMoveDown: boolean
    isEmpty: boolean
    onMove: (direction: "up" | "down") => void
  } | null
}

/**
 * One section's heading — a grid item of its own on GC Default, which is what
 * lets a whole section be dragged and lets a widget be dragged under a
 * different heading.
 *
 * It stays OUTSIDE the cards it introduces, with no border or background of
 * its own, so the page never nests a bordered header inside a bordered card.
 * It is the section nav's scroll target too, hence the anchor and
 * `tabIndex={-1}`.
 */
export const HomeSectionHeading = ({
  id,
  caption,
  action,
  editing
}: HomeSectionHeadingProps) => {
  const { t } = useTranslation()
  const spec = HOME_SECTION_SPECS.find((candidate) => candidate.id === id)
  if (!spec) return null
  const title = t(spec.titleKey, spec.title)

  return (
    <div
      id={sectionAnchorId(id)}
      tabIndex={-1}
      className="home-section-heading"
      data-testid={`home-section-heading-${id}`}
      style={{ scrollMarginTop: SCROLL_MARGIN }}
    >
      {editing ? (
        <WidgetDragHandle
          title={title}
          onMove={(direction) => {
            if (direction === "up" && editing.canMoveUp) editing.onMove("up")
            if (direction === "down" && editing.canMoveDown)
              editing.onMove("down")
          }}
        />
      ) : null}
      <h2
        id={`${sectionAnchorId(id)}-title`}
        className="m-0"
        style={{ ...homeText.sectionTitle, color: homeColor.ink }}
      >
        {title}
      </h2>
      <span
        className="home-section-heading__caption"
        style={{ ...homeText.meta, color: homeColor.inkMuted }}
      >
        {editing?.isEmpty
          ? t(
              "home.layout.emptySection",
              "No widgets here yet — drag one in, or use Move to section."
            )
          : (caption ?? t(spec.captionKey, spec.caption))}
      </span>
      <span className="home-section-heading__action">
        {action}
        {editing ? (
          <>
            <Tooltip title={t("home.layout.sectionUp", "Move section up")}>
              <Button
                type="text"
                size="small"
                icon={<ArrowUpOutlined />}
                disabled={!editing.canMoveUp}
                aria-label={t(
                  "home.layout.sectionUpNamed",
                  "Move {{title}} up",
                  {
                    title
                  }
                )}
                data-testid={`home-section-up-${id}`}
                onClick={() => editing.onMove("up")}
              />
            </Tooltip>
            <Tooltip title={t("home.layout.sectionDown", "Move section down")}>
              <Button
                type="text"
                size="small"
                icon={<ArrowDownOutlined />}
                disabled={!editing.canMoveDown}
                aria-label={t(
                  "home.layout.sectionDownNamed",
                  "Move {{title}} down",
                  { title }
                )}
                data-testid={`home-section-down-${id}`}
                onClick={() => editing.onMove("down")}
              />
            </Tooltip>
          </>
        ) : null}
      </span>
    </div>
  )
}
